// pages/ChatGroups.jsx — Redesigned UI (#004aad theme), @mention dropdown, fully responsive
// + STL/PLY 3D preview & viewer, image preview & fullscreen lightbox
// + Back button (mobile: back to list; desktop: back to previous page)
import React, {
  useEffect, useMemo, useState, useCallback, useRef, lazy, Suspense
} from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';   // ✅ NEW
import {
  Container, Row, Col, Button, Spinner, Form, Modal, ListGroup, Badge
} from 'react-bootstrap';
import {
  FiPlus, FiSend, FiUsers, FiMessageCircle, FiPaperclip, FiX,
  FiFile, FiImage, FiDownload, FiCheck, FiCheckCircle, FiInfo, FiArrowLeft
} from 'react-icons/fi';
import { useSocket } from '../context/SocketContext';
import {
  fetchMyGroups,
  fetchGroupMessages,
  createGroup,
  fetchLabUsers,
  getOrCreateDirectConversation,
  sendMessageHttp,
  markMessagesSeenApi
} from '../api/chatApi';
import '../styles/ChatGroups.css';

// ⚠️ In dono paths ko apne project ke mutabiq adjust karein
import LazySTLThumbnail from '../components/dashboard/cases/newCase/case_id/caseTable/LazySTLThumbnail';
const STLViewer = lazy(() =>
  import('../components/dashboard/cases/newCase/case_id/caseTable/STLViewer')
);

const getUserRole = () => (localStorage.getItem('userRole') || '').toLowerCase();

const getMyObjectId = () => {
  try {
    const u = JSON.parse(localStorage.getItem('userData') || '{}');
    return String(u._id || u.id || localStorage.getItem('_id') || '');
  } catch {
    return '';
  }
};

const API_BASE = 'https://hms.backend.hiragroupcloud.com';

const renderFileIcon = (type) => {
  if (type === 'image') return <FiImage />;
  return <FiFile />;
};

const fileSizeStr = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatSeenTime = (dateStr) => {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleString();
  } catch {
    return '';
  }
};

const getInitials = (name) => {
  const parts = String(name || '?').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const escapeRegExp = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/* ---------- Attachment helpers ---------- */
const IMAGE_EXTS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'];
const MODEL_EXTS = ['stl', 'ply'];

const getAttachmentExt = (a) => {
  const n = String(a?.name || a?.path || '').toLowerCase();
  const m = n.match(/\.([a-z0-9]+)$/);
  return m ? m[1] : '';
};

const isImageAttachment = (a) =>
  a?.type === 'image' || IMAGE_EXTS.includes(getAttachmentExt(a));

const is3DAttachment = (a) => MODEL_EXTS.includes(getAttachmentExt(a));

const getAttachmentUrl = (a) => {
  const p = a?.path || '';
  if (/^https?:\/\//i.test(p)) return p;
  return `${API_BASE}${p.startsWith('/') ? '' : '/'}${p}`;
};

const formatViewerTime = (raw) => {
  if (!raw) return 'Unknown';
  try {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return 'Unknown';
    return d.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch {
    return 'Unknown';
  }
};

const renderTextWithMentions = (text, mentionList, isMine) => {
  if (!text) return null;
  const names = (Array.isArray(mentionList) ? mentionList : [])
    .map((mn) => mn?.userName)
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);

  if (names.length === 0) return text;

  const regex = new RegExp(`(@(?:${names.map(escapeRegExp).join('|')}))`, 'g');
  const parts = text.split(regex);

  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <span key={i} className={`cg-mention-chip ${isMine ? 'mine' : ''}`}>{part}</span>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
};

/* ---------- Image attachment (inline preview) ---------- */
function ImageAttachment({ attachment, isMine, onOpen }) {
  const [failed, setFailed] = useState(false);
  const url = getAttachmentUrl(attachment);

  if (failed) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={`cg-attach ${isMine ? 'mine' : 'theirs'}`}
      >
        {renderFileIcon('image')}
        <span className="text-truncate" style={{ maxWidth: 160 }}>{attachment.name}</span>
        <span style={{ opacity: 0.7 }}>({fileSizeStr(attachment.size)})</span>
        <FiDownload size={12} />
      </a>
    );
  }

  return (
    <div style={{ marginBottom: 4 }}>
      <img
        src={url}
        alt={attachment.name || 'image'}
        loading="lazy"
        onClick={() => onOpen(attachment)}
        onError={() => setFailed(true)}
        title="Click to view full screen"
        style={{
          display: 'block',
          width: '100%',
          maxWidth: 240,
          maxHeight: 220,
          objectFit: 'cover',
          borderRadius: 10,
          cursor: 'zoom-in',
          background: 'rgba(255,255,255,0.15)'
        }}
      />
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 11,
          marginTop: 3,
          opacity: 0.85,
          maxWidth: 240
        }}
      >
        <span className="text-truncate" style={{ flex: 1, minWidth: 0 }}>{attachment.name}</span>
        <span style={{ flexShrink: 0 }}>{fileSizeStr(attachment.size)}</span>
      </div>
    </div>
  );
}

/* ---------- 3D (STL / PLY) attachment ---------- */
function ModelAttachment({ attachment, cacheKey, isMine, onOpen }) {
  const url = getAttachmentUrl(attachment);

  return (
    <div style={{ marginBottom: 4 }}>
      <div
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 240,
          height: 160,
          borderRadius: 10,
          overflow: 'hidden',
          background: '#fff',
          border: `1px solid ${isMine ? 'rgba(255,255,255,0.35)' : '#e3e7ed'}`
        }}
      >
        <LazySTLThumbnail
          fileUrl={url}
          cacheKey={cacheKey}
          height="100%"
          onViewClick={() => onOpen(attachment)}
        />
        <div
          onClick={() => onOpen(attachment)}
          title="Click to open 3D viewer"
          style={{ position: 'absolute', inset: 0, cursor: 'pointer', zIndex: 2 }}
        />
        <span
          style={{
            position: 'absolute',
            right: 6,
            bottom: 6,
            zIndex: 3,
            pointerEvents: 'none',
            background: '#004aad',
            color: '#fff',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '.04em',
            padding: '2px 8px',
            borderRadius: 999
          }}
        >
          VIEW 3D
        </span>
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 11,
          marginTop: 3,
          opacity: 0.85,
          maxWidth: 240
        }}
      >
        <span className="text-truncate" style={{ flex: 1, minWidth: 0 }}>{attachment.name}</span>
        <span style={{ flexShrink: 0 }}>{fileSizeStr(attachment.size)}</span>
        <a
          href={url}
          download={attachment.name}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          title="Download"
          style={{ color: 'inherit', display: 'inline-flex', flexShrink: 0 }}
        >
          <FiDownload size={12} />
        </a>
      </div>
    </div>
  );
}

/* ---------- Full screen image lightbox ---------- */
function ImageLightbox({ preview, onClose }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const btnStyle = {
    width: 38, height: 38, borderRadius: 10,
    border: '1px solid rgba(255,255,255,0.25)',
    background: 'rgba(255,255,255,0.14)', color: '#fff',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', textDecoration: 'none'
  };

  return ReactDOM.createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'rgba(0,0,0,0.88)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 12, boxSizing: 'border-box'
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'absolute', top: 0, left: 0, right: 0,
          padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 12,
          color: '#fff', background: 'linear-gradient(180deg, rgba(0,0,0,0.6), transparent)'
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="text-truncate" style={{ fontWeight: 700, fontSize: 15 }} title={preview.name}>
            {preview.name}
          </div>
          <div style={{ fontSize: 11, opacity: 0.75 }}>
            {preview.uploadedBy ? `${preview.uploadedBy} · ` : ''}
            {formatViewerTime(preview.uploadedAt)}
            {preview.size ? ` · ${fileSizeStr(preview.size)}` : ''}
          </div>
        </div>
        <a href={preview.url} download={preview.name} target="_blank" rel="noopener noreferrer" style={btnStyle} title="Download" aria-label="Download image">
          <FiDownload />
        </a>
        <button type="button" onClick={onClose} style={btnStyle} title="Close" aria-label="Close image viewer">
          <FiX size={18} />
        </button>
      </div>

      <img
        src={preview.url}
        alt={preview.name || 'image'}
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: '94vw', maxHeight: '86vh', objectFit: 'contain',
          borderRadius: 6, boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
        }}
      />
    </div>,
    document.body
  );
}

/* ---------- Full screen 3D viewer ---------- */
function Chat3DViewerModal({ preview, onClose }) {
  const [isModelReady, setIsModelReady] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleLoad = useCallback(() => setIsModelReady(true), []);
  const handleError = useCallback(() => setIsModelReady(true), []);

  return ReactDOM.createPortal(
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, width: '100vw', height: '100vh',
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.85)', zIndex: 10000,
        padding: 6, boxSizing: 'border-box'
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(1800px, 96vw)', height: 'min(900px, 92vh)',
          backgroundColor: '#00a1d6', borderRadius: 10,
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)', overflow: 'hidden'
        }}
      >
        <div
          style={{
            backgroundColor: '#004aad',
            backgroundImage: 'linear-gradient(115deg, #004aad 0%, #003b88 100%)',
            color: '#fff', padding: '12px 16px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12, flexShrink: 0, boxSizing: 'border-box',
            borderBottom: '1px solid rgba(255,255,255,0.18)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: '1 1 auto' }}>
            <div style={{
              width: 38, height: 38, minWidth: 38,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: 'rgba(255,255,255,0.13)',
              border: '1px solid rgba(255,255,255,0.2)', borderRadius: 10
            }}>
              <FiFile size={18} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{
                fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap',
                overflow: 'hidden', textOverflow: 'ellipsis'
              }} title={preview.name}>
                {preview.name || 'File'}
              </div>
              <div style={{
                fontSize: 10, color: 'rgba(255,255,255,0.72)',
                letterSpacing: '0.7px', textTransform: 'uppercase', fontWeight: 500
              }}>
                3D Model Viewer
                {preview.uploadedBy ? ` · ${preview.uploadedBy}` : ''}
                {preview.uploadedAt ? ` · ${formatViewerTime(preview.uploadedAt)}` : ''}
              </div>
            </div>
          </div>

          <button
            type="button" onClick={onClose}
            aria-label="Close 3D viewer" title="Close viewer"
            style={{
              backgroundColor: 'rgba(255,255,255,0.12)', color: '#fff',
              border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8,
              width: 34, height: 34, display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', flexShrink: 0
            }}
          >
            <FiX size={18} />
          </button>
        </div>

        <div style={{ position: 'relative', flex: 1, minHeight: 0, width: '100%' }}>
          {!isModelReady && (
            <div style={{
              position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center',
              backgroundColor: '#00a1d6', zIndex: 5
            }}>
              <div style={{
                width: 50, height: 50,
                border: '4px solid rgba(255,255,255,0.3)',
                borderTopColor: '#fff', borderRadius: '50%',
                animation: 'cg-stl-spin 0.8s linear infinite'
              }} />
              <p style={{ color: '#fff', marginTop: 15, fontSize: 14 }}>
                Loading 3D Model...
                {progress > 0 && progress < 100 ? ` ${progress}%` : ''}
              </p>
              <style>{`@keyframes cg-stl-spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          <div style={{ width: '100%', height: '100%' }}>
            <Suspense fallback={null}>
              <STLViewer
                fileUrl={preview.url}
                size="extra-large"
                shouldLoad={true}
                onProgress={setProgress}
                onLoad={handleLoad}
                onError={handleError}
              />
            </Suspense>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function ChatGroups() {
  const {
    joinGroupRoom,
    leaveGroupRoom,
    chatMessagesByGroup,
    chatUnreadByGroup,
    resetGroupUnread,
    setGroupMessages,
    markGroupMessagesSeen,
    socket
  } = useSocket();

  const navigate = useNavigate(); // ✅ NEW: for back navigation

  const [groups, setGroups] = useState([]);
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState('');
  const [pendingFiles, setPendingFiles] = useState([]);
  const [mentions, setMentions] = useState([]);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [showNewDM, setShowNewDM] = useState(false);
  const [sending, setSending] = useState(false);

  const [infoMessage, setInfoMessage] = useState(null);
  const [filePreview, setFilePreview] = useState(null);

  const [mentionQuery, setMentionQuery] = useState(null);
  const [mentionStart, setMentionStart] = useState(-1);
  const [mentionIndex, setMentionIndex] = useState(0);

  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const mentionListRef = useRef(null);

  const isAdmin = getUserRole() === 'admin';
  const myObjectId = getMyObjectId();

  const activeGroup = useMemo(
    () => groups.find((g) => String(g._id) === String(activeGroupId)) || null,
    [groups, activeGroupId]
  );

  const activeMessages = chatMessagesByGroup[String(activeGroupId)] || [];

  const mentionCandidates = useMemo(() => {
    if (mentionQuery === null || !activeGroup) return [];
    const q = mentionQuery.toLowerCase();
    return (activeGroup.members || [])
      .filter((m) => String(m.userId) !== String(myObjectId))
      .filter((m) => (m.userName || '').toLowerCase().includes(q))
      .slice(0, 20);
  }, [mentionQuery, activeGroup, myObjectId]);

  const loadGroups = useCallback(async () => {
    setLoadingGroups(true);
    setError(null);
    try {
      const data = await fetchMyGroups();
      if (data.success) setGroups(data.groups || []);
      else setError(data.message || 'Failed to load groups');
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load groups');
    } finally {
      setLoadingGroups(false);
    }
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const closeMentionDropdown = () => {
    setMentionQuery(null);
    setMentionStart(-1);
    setMentionIndex(0);
  };

  const handleOpenAttachment = useCallback((attachment, message) => {
    if (!attachment) return;
    setFilePreview({
      kind: is3DAttachment(attachment) ? '3d' : 'image',
      url: getAttachmentUrl(attachment),
      name: attachment.name || '',
      size: attachment.size,
      uploadedBy: message?.senderName || '',
      uploadedAt: message?.createdAt || null
    });
  }, []);

  const handleClosePreview = useCallback(() => setFilePreview(null), []);

  const openGroup = useCallback(
    async (group) => {
      if (!group) return;
      if (activeGroupId && activeGroupId !== group._id) {
        leaveGroupRoom(activeGroupId);
      }
      setActiveGroupId(group._id);
      setPendingFiles([]);
      setMentions([]);
      setDraft('');
      setMentionQuery(null);
      setMentionStart(-1);
      setMentionIndex(0);
      resetGroupUnread(group._id);

      const ack = await joinGroupRoom(group._id);
      if (!ack?.success) {
        setError(ack?.message || 'Failed to join group');
        return;
      }

      if (!chatMessagesByGroup[String(group._id)]) {
        setLoadingMessages(true);
        try {
          const data = await fetchGroupMessages(group._id, { limit: 100 });
          if (data.success) setGroupMessages(group._id, data.messages || []);
        } catch (e) {
          setError(e.response?.data?.message || 'Failed to load messages');
        } finally {
          setLoadingMessages(false);
        }
      }
    },
    [
      activeGroupId,
      leaveGroupRoom,
      joinGroupRoom,
      resetGroupUnread,
      chatMessagesByGroup,
      setGroupMessages
    ]
  );

  /* Mobile: chat se wapas list par jaana */
  const handleBackToList = () => {
    if (activeGroupId) leaveGroupRoom(activeGroupId);
    setActiveGroupId(null);
    setDraft('');
    setMentions([]);
    setPendingFiles([]);
    setFilePreview(null);
    closeMentionDropdown();
  };

  /* ✅ NEW: Desktop: chat se bahar nikle — browser history se previous page */
  const handleBackToPreviousPage = () => {
    if (activeGroupId) leaveGroupRoom(activeGroupId);
    // Prefer going back to previous page; fallback to /home
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/home');
    }
  };

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeMessages.length, activeGroupId]);

  // Auto-mark-seen
  useEffect(() => {
    if (!activeGroupId) return;
    if (activeMessages.length === 0) return;

    const myId = String(myObjectId || '');
    if (!myId) return;

    const unreadIds = activeMessages
      .filter((m) => {
        if (String(m.senderId) === myId) return false;
        const seenBy = Array.isArray(m.seenBy) ? m.seenBy : [];
        return !seenBy.some((s) => String(s.userIdObjectId) === myId);
      })
      .map((m) => m._id);

    if (unreadIds.length === 0) return;

    if (typeof markGroupMessagesSeen === 'function') {
      markGroupMessagesSeen(activeGroupId, unreadIds).catch(() => {
        markMessagesSeenApi(activeGroupId, unreadIds).catch(() => {});
      });
    } else {
      markMessagesSeenApi(activeGroupId, unreadIds).catch(() => {});
    }
  }, [activeGroupId, activeMessages.length, myObjectId, markGroupMessagesSeen, socket]);

  // Textarea auto-resize
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [draft, activeGroupId]);

  useEffect(() => {
    if (mentionQuery === null || !mentionListRef.current) return;
    const el = mentionListRef.current.querySelector('.cg-mention-item.active');
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [mentionIndex, mentionQuery, mentionCandidates.length]);

  const updateMentionState = (value, caret) => {
    const before = value.slice(0, caret);
    const match = /(^|\s)@([^\s@]*)$/.exec(before);
    if (match && activeGroup) {
      setMentionQuery(match[2]);
      setMentionStart(before.length - match[2].length - 1);
      setMentionIndex(0);
    } else {
      closeMentionDropdown();
    }
  };

  const handleDraftChange = (e) => {
    const value = e.target.value;
    const caret = e.target.selectionStart ?? value.length;
    setDraft(value);
    setMentions((prev) => prev.filter((m) => value.includes(`@${m.userName}`)));
    updateMentionState(value, caret);
  };

  const selectMention = (member) => {
    if (!member) return;
    const el = textareaRef.current;
    const caret = el ? el.selectionStart : draft.length;
    const beforeAt = draft.slice(0, mentionStart);
    const after = draft.slice(caret);
    const insert = `@${member.userName} `;
    const newText = beforeAt + insert + after;

    setDraft(newText);
    setMentions((prev) =>
      prev.some((m) => String(m.userId) === String(member.userId))
        ? prev
        : [...prev, { userId: member.userId, userName: member.userName }]
    );
    closeMentionDropdown();

    const newCaret = (beforeAt + insert).length;
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCaret, newCaret);
      }
    }, 0);
  };

  const handleKeyDown = (e) => {
    const dropdownOpen = mentionQuery !== null;

    if (dropdownOpen && mentionCandidates.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setMentionIndex((i) => (i + 1) % mentionCandidates.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setMentionIndex((i) => (i - 1 + mentionCandidates.length) % mentionCandidates.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        selectMention(mentionCandidates[mentionIndex]);
        return;
      }
    }

    if (dropdownOpen && e.key === 'Escape') {
      e.preventDefault();
      closeMentionDropdown();
      return;
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleCaretMove = (e) => {
    updateMentionState(e.target.value, e.target.selectionStart ?? e.target.value.length);
  };

  const handleAttachClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setPendingFiles((prev) => [...prev, ...files].slice(0, 10));
    e.target.value = '';
  };

  const removeFile = (idx) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSend = async () => {
    if (sending) return;
    const text = draft.trim();
    const hasFiles = pendingFiles.length > 0;
    if (!text && !hasFiles) return;
    if (!activeGroupId) return;

    setSending(true);
    setError(null);

    try {
      const activeMentions = mentions.filter((m) => text.includes(`@${m.userName}`));
      const mentionedUserIds = activeMentions.map((m) => m.userId);
      const data = await sendMessageHttp(
        activeGroupId,
        text,
        pendingFiles,
        mentionedUserIds
      );

      if (data.success && data.message) {
        setGroupMessages(activeGroupId, [
          ...(chatMessagesByGroup[String(activeGroupId)] || []),
          data.message
        ]);
        setDraft('');
        setPendingFiles([]);
        setMentions([]);
        closeMentionDropdown();
      } else {
        setError(data.message || 'Failed to send message');
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleStartDM = async (otherUserObjectId) => {
    try {
      const data = await getOrCreateDirectConversation(otherUserObjectId);
      if (data.success && data.group) {
        setShowNewDM(false);
        setGroups((prev) => {
          if (prev.some((g) => String(g._id) === String(data.group._id))) return prev;
          return [data.group, ...prev];
        });
        await openGroup(data.group);
      } else {
        setError(data.message || 'Failed to create conversation');
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to create conversation');
    }
  };

  const getGroupDisplayName = (g) => {
    if (!g) return '';
    if (g.isDirect) {
      const other = (g.members || []).find(
        (m) => String(m.userId) !== String(myObjectId)
      );
      return other?.userName || g.name || 'Direct Message';
    }
    return g.name;
  };

  const getSeenStatus = (msg) => {
    if (String(msg.senderId) !== String(myObjectId)) return null;
    const group = activeGroup;
    if (!group) return null;

    const totalMembers = (group.members || []).length;
    const seenBy = Array.isArray(msg.seenBy) ? msg.seenBy : [];
    const seenCount = seenBy.length;

    if (seenCount >= totalMembers) {
      return { icon: 'double', color: '#7fe3ff', tooltip: 'Seen by all' };
    }
    if (seenCount > 1) {
      return { icon: 'double', color: 'rgba(255,255,255,0.75)', tooltip: `Seen by ${seenCount - 1}` };
    }
    return { icon: 'single', color: 'rgba(255,255,255,0.75)', tooltip: 'Sent' };
  };

  return (
    <Container fluid className="cg-root">
      <div className="cg-shell">
        <Row className="g-0 h-100 flex-nowrap">
          {/* LEFT */}
          <Col
            xs={12}
            md={4}
            lg={4}
            xl={3}
            className={`cg-sidebar ${activeGroup ? 'd-none d-md-flex' : 'd-flex'}`}
          >
            {/* ✅ NEW: Back-to-previous-page button on sidebar header (desktop + mobile) */}
            <div className="cg-sidebar-header">
              <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                <button
                  type="button"
                  className="cg-header-icon-btn"
                  onClick={handleBackToPreviousPage}
                  title="Back"
                  aria-label="Back"
                >
                  <FiArrowLeft size={16} />
                </button>
                <h5 className="mb-0">Chats</h5>
              </div>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="cg-header-btn"
                  onClick={() => setShowNewDM(true)}
                  title="New Direct Message"
                >
                  <FiMessageCircle />
                </button>
                {isAdmin && (
                  <button
                    type="button"
                    className="cg-header-btn"
                    onClick={() => setShowCreateGroup(true)}
                    title="New Group"
                  >
                    <FiPlus /> New
                  </button>
                )}
              </div>
            </div>

            <div className="cg-sidebar-list">
              {loadingGroups ? (
                <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
              ) : error && groups.length === 0 ? (
                <div className="text-danger small p-2">{error}</div>
              ) : groups.length === 0 ? (
                <div className="text-muted small p-2">No chats yet. Start a DM or create a group.</div>
              ) : (
                groups.map((g) => {
                  const unread = chatUnreadByGroup[String(g._id)] || 0;
                  const active = String(g._id) === String(activeGroupId);
                  const name = getGroupDisplayName(g);
                  return (
                    <button
                      key={g._id}
                      type="button"
                      onClick={() => openGroup(g)}
                      className={`cg-chat-item ${active ? 'active' : ''}`}
                    >
                      <div className={`cg-avatar ${active ? 'solid' : ''}`}>
                        {g.isDirect ? getInitials(name) : <FiUsers size={18} />}
                      </div>
                      <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <div className="title text-truncate">{name}</div>
                        <div className="sub">
                          {g.isDirect ? 'Direct Message' : `${g.members?.length || 0} members`}
                        </div>
                      </div>
                      {unread > 0 && <span className="cg-unread">{unread}</span>}
                    </button>
                  );
                })
              )}
            </div>
          </Col>

          {/* RIGHT */}
          <Col
            xs={12}
            md={8}
            lg={8}
            xl={9}
            className={`cg-main ${activeGroup ? 'd-flex' : 'd-none d-md-flex'}`}
          >
            {!activeGroup ? (
              <div className="cg-empty">
                <div className="icon"><FiMessageCircle size={32} /></div>
                <h6 className="mb-1" style={{ color: '#1f2937' }}>Your messages</h6>
                <div className="small">Select a chat to start messaging</div>
              </div>
            ) : (
              <>
                <div className="cg-main-header">
                  {/* Mobile: back-to-list */}
                  <button
                    type="button"
                    className="cg-back-btn d-md-none"
                    onClick={handleBackToList}
                    aria-label="Back to chat list"
                    title="Back to chat list"
                  >
                    <FiArrowLeft />
                  </button>
                  {/* ✅ NEW: Desktop: back-to-previous-page */}
                  <button
                    type="button"
                    className="cg-back-btn d-none d-md-inline-flex"
                    onClick={handleBackToPreviousPage}
                    aria-label="Back"
                    title="Back"
                  >
                    <FiArrowLeft />
                  </button>
                  <div className="cg-avatar solid">
                    {activeGroup.isDirect
                      ? getInitials(getGroupDisplayName(activeGroup))
                      : <FiUsers size={18} />}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div className="fw-semibold text-truncate" style={{ color: '#1f2937' }}>
                      {getGroupDisplayName(activeGroup)}
                    </div>
                    <small className="text-muted">
                      {activeGroup.isDirect
                        ? 'Direct Message'
                        : `${activeGroup.members?.length || 0} members`}
                    </small>
                  </div>
                </div>

                <div className="cg-messages">
                  {loadingMessages ? (
                    <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
                  ) : activeMessages.length === 0 ? (
                    <div className="text-muted small text-center py-4">No messages yet. Say hi 👋</div>
                  ) : (
                    activeMessages.map((m) => {
                      const isMine = String(m.senderId) === String(myObjectId);
                      const seenStatus = getSeenStatus(m);

                      return (
                        <div key={m._id} className={`cg-msg-row ${isMine ? 'mine' : ''}`}>
                          <div
                            className={`cg-bubble ${isMine ? 'mine' : 'theirs'}`}
                            onContextMenu={(e) => {
                              e.preventDefault();
                              setInfoMessage(m);
                            }}
                          >
                            {!isMine && (
                              <div className="cg-sender">{m.senderName || 'User'}</div>
                            )}

                            {m.text && (
                              <div style={{ whiteSpace: 'pre-wrap' }}>
                                {renderTextWithMentions(m.text, m.mentions, isMine)}
                              </div>
                            )}

                            {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                              <div className="mt-2">
                                {m.attachments.map((a, i) => {
                                  if (isImageAttachment(a)) {
                                    return (
                                      <ImageAttachment
                                        key={i}
                                        attachment={a}
                                        isMine={isMine}
                                        onOpen={(att) => handleOpenAttachment(att, m)}
                                      />
                                    );
                                  }
                                  if (is3DAttachment(a)) {
                                    return (
                                      <ModelAttachment
                                        key={i}
                                        attachment={a}
                                        cacheKey={`${m._id}-${i}-${a.size || ''}`}
                                        isMine={isMine}
                                        onOpen={(att) => handleOpenAttachment(att, m)}
                                      />
                                    );
                                  }
                                  return (
                                    <a
                                      key={i}
                                      href={getAttachmentUrl(a)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={`cg-attach ${isMine ? 'mine' : 'theirs'}`}
                                    >
                                      {renderFileIcon(a.type)}
                                      <span className="text-truncate" style={{ maxWidth: 160 }}>
                                        {a.name}
                                      </span>
                                      <span style={{ opacity: 0.7 }}>({fileSizeStr(a.size)})</span>
                                      <FiDownload size={12} />
                                    </a>
                                  );
                                })}
                              </div>
                            )}

                            <div className="d-flex justify-content-between align-items-center mt-1">
                              <span className="cg-time">
                                {new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                              {seenStatus && (
                                <span title={seenStatus.tooltip} style={{ marginLeft: 8 }}>
                                  {seenStatus.icon === 'double' ? (
                                    <FiCheckCircle size={12} color={seenStatus.color} />
                                  ) : (
                                    <FiCheck size={12} color={seenStatus.color} />
                                  )}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                <div className="cg-composer">
                  {mentionQuery !== null && (
                    <div className="cg-mention-dropdown" onMouseDown={(e) => e.preventDefault()}>
                      <div className="cg-mention-head">Mention a member</div>
                      {mentionCandidates.length === 0 ? (
                        <div className="cg-mention-empty">No members found</div>
                      ) : (
                        <div className="cg-mention-list" ref={mentionListRef}>
                          {mentionCandidates.map((m, i) => (
                            <button
                              key={m.userId || i}
                              type="button"
                              className={`cg-mention-item ${i === mentionIndex ? 'active' : ''}`}
                              onClick={() => selectMention(m)}
                              onMouseEnter={() => setMentionIndex(i)}
                            >
                              <span className="cg-avatar sm">{getInitials(m.userName)}</span>
                              <span className="text-truncate fw-semibold" style={{ fontSize: 14 }}>
                                {m.userName || 'User'}
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {error && (
                    <div className="small text-danger mb-2">{error}</div>
                  )}

                  {pendingFiles.length > 0 && (
                    <div className="cg-pending">
                      {pendingFiles.map((f, i) => (
                        <div key={i} className="cg-pending-item">
                          <FiFile size={12} />
                          <span className="text-truncate" style={{ maxWidth: 140 }}>{f.name}</span>
                          <span style={{ opacity: 0.7 }}>({fileSizeStr(f.size)})</span>
                          <FiX
                            role="button"
                            style={{ cursor: 'pointer' }}
                            onClick={() => removeFile(i)}
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleSend();
                    }}
                    className="cg-input-row"
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      style={{ display: 'none' }}
                      onChange={handleFileSelect}
                    />
                    <button
                      type="button"
                      className="cg-icon-btn"
                      onClick={handleAttachClick}
                      disabled={sending || pendingFiles.length >= 10}
                      title="Attach files"
                    >
                      <FiPaperclip />
                    </button>
                    <textarea
                      ref={textareaRef}
                      rows={1}
                      className="cg-textarea"
                      value={draft}
                      onChange={handleDraftChange}
                      onKeyDown={handleKeyDown}
                      onKeyUp={(e) => {
                        if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) handleCaretMove(e);
                      }}
                      onClick={handleCaretMove}
                      placeholder="Type a message… use @ to mention"
                      disabled={sending}
                    />
                    <button
                      type="submit"
                      className="cg-send-btn"
                      disabled={sending || (!draft.trim() && pendingFiles.length === 0)}
                      title="Send"
                    >
                      {sending ? <Spinner size="sm" animation="border" /> : <FiSend />}
                    </button>
                  </Form>
                </div>
              </>
            )}
          </Col>
        </Row>
      </div>

      {isAdmin && (
        <CreateGroupModal
          show={showCreateGroup}
          onHide={() => setShowCreateGroup(false)}
          onCreated={async (created) => {
            setShowCreateGroup(false);
            setGroups((prev) => {
              if (prev.some((g) => String(g._id) === String(created._id))) return prev;
              return [created, ...prev];
            });
            await openGroup(created);
          }}
        />
      )}

      <NewDMModal
        show={showNewDM}
        onHide={() => setShowNewDM(false)}
        myObjectId={myObjectId}
        onSelect={handleStartDM}
      />

      <MessageInfoModal
        show={!!infoMessage}
        onHide={() => setInfoMessage(null)}
        message={infoMessage}
        members={activeGroup?.members || []}
        myObjectId={myObjectId}
      />

      {filePreview && filePreview.kind === 'image' && (
        <ImageLightbox preview={filePreview} onClose={handleClosePreview} />
      )}
      {filePreview && filePreview.kind === '3d' && (
        <Chat3DViewerModal preview={filePreview} onClose={handleClosePreview} />
      )}
    </Container>
  );
}

/* ---------- Create Group Modal ---------- */
function CreateGroupModal({ show, onHide, onCreated }) {
  const [name, setName] = useState('');
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [err, setErr] = useState(null);
  const myObjectId = getMyObjectId();

  useEffect(() => {
    if (!show) return;
    setName('');
    setSelected([]);
    setErr(null);

    (async () => {
      setLoadingUsers(true);
      try {
        const data = await fetchLabUsers();
        if (data.success) {
          setUsers((data.users || []).filter((u) => String(u._id) !== String(myObjectId)));
        }
      } catch (e) {
        setErr(e.response?.data?.message || 'Failed to load users');
      } finally {
        setLoadingUsers(false);
      }
    })();
  }, [show, myObjectId]);

  const toggle = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const submit = async () => {
    if (!name.trim()) {
      setErr('Group name is required');
      return;
    }
    setLoading(true);
    setErr(null);
    try {
      const data = await createGroup({ name: name.trim(), memberUserIds: selected });
      if (data.success) onCreated(data.group);
      else setErr(data.message || 'Failed to create group');
    } catch (e) {
      setErr(e.response?.data?.message || 'Failed to create group');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered className="cg-modal">
      <Modal.Header closeButton><Modal.Title>Create Chat Group</Modal.Title></Modal.Header>
      <Modal.Body>
        {err && <div className="alert alert-danger py-1 small">{err}</div>}
        <Form.Group className="mb-3">
          <Form.Label>Group Name</Form.Label>
          <Form.Control value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Front Desk Team" />
        </Form.Group>
        <Form.Group>
          <Form.Label>Add Members</Form.Label>
          {loadingUsers ? (
            <div><Spinner animation="border" size="sm" /></div>
          ) : users.length === 0 ? (
            <div className="text-muted small">No other users in your lab.</div>
          ) : (
            <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid #e3e7ed', borderRadius: 10, padding: 8 }}>
              {users.map((u) => (
                <Form.Check
                  key={u._id}
                  type="checkbox"
                  id={`member-${u._id}`}
                  className="py-1"
                  label={[u.first_name, u.last_name].filter(Boolean).join(' ') || u.username || u.email || 'User'}
                  checked={selected.includes(u._id)}
                  onChange={() => toggle(u._id)}
                />
              ))}
            </div>
          )}
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>Cancel</Button>
        <Button variant="primary" onClick={submit} disabled={loading}>
          {loading ? <Spinner size="sm" animation="border" /> : 'Create'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/* ---------- New DM Modal ---------- */
function NewDMModal({ show, onHide, myObjectId, onSelect }) {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!show) return;
    setSearch('');
    setErr(null);

    (async () => {
      setLoading(true);
      try {
        const data = await fetchLabUsers();
        if (data.success) {
          setUsers((data.users || []).filter((u) => String(u._id) !== String(myObjectId)));
        }
      } catch (e) {
        setErr(e.response?.data?.message || 'Failed to load users');
      } finally {
        setLoading(false);
      }
    })();
  }, [show, myObjectId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const name = `${u.first_name || ''} ${u.last_name || ''} ${u.username || ''} ${u.email || ''}`.toLowerCase();
      return name.includes(q);
    });
  }, [users, search]);

  return (
    <Modal show={show} onHide={onHide} centered className="cg-modal">
      <Modal.Header closeButton>
        <Modal.Title><FiMessageCircle className="me-2" />New Direct Message</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {err && <div className="alert alert-danger py-1 small">{err}</div>}
        <Form.Control
          size="sm"
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="mb-2"
        />
        {loading ? (
          <div className="text-center py-3"><Spinner animation="border" size="sm" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-muted small">No users found in your lab.</div>
        ) : (
          <div style={{ maxHeight: 340, overflowY: 'auto' }}>
            {filtered.map((u) => {
              const fullName =
                [u.first_name, u.last_name].filter(Boolean).join(' ') || u.username || u.email || 'User';
              return (
                <button
                  key={u._id}
                  type="button"
                  className="cg-user-btn"
                  onClick={() => onSelect(u._id)}
                >
                  <span className="cg-avatar sm">{getInitials(fullName)}</span>
                  <span style={{ minWidth: 0 }}>
                    <div className="fw-semibold text-truncate">{fullName}</div>
                    {u.username && u.username !== u.first_name && (
                      <div className="small text-muted">@{u.username}</div>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancel</Button>
      </Modal.Footer>
    </Modal>
  );
}

/* ---------- Message Info Modal ---------- */
function MessageInfoModal({ show, onHide, message, members = [], myObjectId }) {
  if (!message) return null;

  const seenBy = Array.isArray(message.seenBy) ? message.seenBy : [];
  const seenIds = new Set(seenBy.map((s) => String(s.userIdObjectId)));

  const notSeen = members.filter(
    (m) => !seenIds.has(String(m.userId))
  );

  return (
    <Modal show={show} onHide={onHide} centered className="cg-modal">
      <Modal.Header closeButton>
        <Modal.Title>
          <FiInfo className="me-2" />
          Message Info
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {message.text && (
          <div className="mb-3 p-2 rounded" style={{ background: '#f4f6f9', fontSize: 13 }}>
            <strong>{message.senderName || 'User'}:</strong> {message.text.slice(0, 200)}
            {message.text.length > 200 ? '…' : ''}
          </div>
        )}
        {Array.isArray(message.attachments) && message.attachments.length > 0 && (
          <div className="mb-3 small text-muted">
            {message.attachments.length} attachment(s)
          </div>
        )}

        <h6 className="mb-2">
          <FiCheckCircle className="me-1" style={{ color: '#004aad' }} />
          Seen by ({seenBy.length})
        </h6>
        {seenBy.length === 0 ? (
          <div className="text-muted small mb-3">No one has seen this yet.</div>
        ) : (
          <ListGroup className="mb-3">
            {seenBy.map((s, idx) => {
              const isMe = String(s.userIdObjectId) === String(myObjectId);
              return (
                <ListGroup.Item
                  key={idx}
                  className="d-flex justify-content-between align-items-center py-2"
                >
                  <div>
                    <strong>{s.userName || 'User'}</strong>
                    {isMe && <Badge bg="secondary" className="ms-2">You</Badge>}
                  </div>
                  <small className="text-muted">{formatSeenTime(s.seenAt)}</small>
                </ListGroup.Item>
              );
            })}
          </ListGroup>
        )}

        <h6 className="mb-2">
          <FiCheck className="me-1 text-secondary" />
          Not seen ({notSeen.length})
        </h6>
        {notSeen.length === 0 ? (
          <div className="text-muted small">Everyone has seen this.</div>
        ) : (
          <ListGroup>
            {notSeen.map((m, idx) => (
              <ListGroup.Item key={idx} className="py-2">
                {m.userName || 'User'}
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Close</Button>
      </Modal.Footer>
    </Modal>
  );
}